import { useEffect, useRef, useState, type ReactNode } from "react";
import { BubbleMenu, EditorContent, useEditor, useEditorState, type Editor } from "@tiptap/react";
import Collaboration from "@tiptap/extension-collaboration";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import Typography from "@tiptap/extension-typography";
import { ySyncPluginKey } from "y-prosemirror";
import { YDOC_FIELD, schemaExtensions, type DocumentMeta, type PMNode } from "@quill/shared";
import type { Transaction } from "@tiptap/pm/state";
import { api } from "../api";
import { BackButton } from "../components/BackButton";
import { MeasureHandles } from "../components/MeasureHandles";
import { ScrollThumb } from "../components/ScrollThumb";
import { openDocSession, type DocSession } from "../docSession";
import { useOnline, useSyncState } from "../hooks";
import { FindBar, type FindRequest } from "../components/FindBar";
import { History } from "../components/History";
import { ProgressButton, ProgressSheet } from "../components/Progress";
import { Find } from "../find";
import { PreserveSpaces } from "../clipboard";
import { FocusBlock, useTypewriter } from "../focus";
import { useFormatBar } from "../formatBar";
import { useKeyboardAwareCaret } from "../keyboard";
import { folderPath, navigate } from "../router";
import { recordWritten } from "../progress";
import { toggleFocusMode, useFocusMode } from "../typography";
import { addToDictionary, suggest } from "../spell/checker";
import { Spellcheck, misspellingAtCaret } from "../spell/plugin";
import type { SyncClient } from "../sync";

interface Props {
  sync: SyncClient;
  userId: string;
  docId: string;
  meta: DocumentMeta | undefined;
  /** In Trash (directly or via its folder): shown read-only. */
  trashed: boolean;
}

export function EditorScreen({ sync, userId, docId, meta, trashed }: Props) {
  const [session, setSession] = useState<DocSession | null>(null);
  const [ready, setReady] = useState(false);
  const [denied, setDenied] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [find, setFind] = useState<FindRequest | null>(null);
  const online = useOnline();
  const focusMode = useFocusMode();
  const openFind = (replace: boolean) => setFind((f) => ({ n: (f?.n ?? 0) + 1, replace }));

  // Ctrl/Cmd+F: Quill's own find, which also searches text not drawn yet. Ctrl+H (Cmd+Option+F): replace.
  // Ctrl/Cmd+Shift+F: focus mode.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (mod && key === "f" && e.shiftKey && !e.altKey) toggleFocusMode();
      else if (mod && key === "f" && !e.shiftKey) openFind(e.altKey);
      else if (e.ctrlKey && !e.metaKey && key === "h") openFind(true);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const s = openDocSession(sync, userId, docId);
    setSession(s);
    setReady(false);
    setDenied(null);
    setNotice(null);
    setShowHistory(false);
    let live = true;
    void s.ready.then(() => live && setReady(true));
    void s.denied.then((reason) => live && setDenied(reason));
    return () => {
      live = false;
      s.close();
    };
  }, [sync, userId, docId]);

  return (
    <main className="editor-screen">
      <MeasureHandles />
      <header className="editor-header">
        <BackButton
          label={trashed ? "Trash" : "Library"}
          onClick={() => navigate(trashed ? "/trash" : folderPath(meta?.folderId ?? null))}
        />
        <div className="editor-actions">
          <button
            className="quiet"
            aria-pressed={focusMode}
            title="Keep the line you're writing in place and dim the rest"
            // Keep the caret in the page, so typing carries on.
            onMouseDown={(e) => e.preventDefault()}
            onClick={toggleFocusMode}
          >
            Focus
          </button>
          <button className="quiet" onClick={() => openFind(false)}>
            Find
          </button>
          {!trashed && (
            <button className="quiet" onClick={() => setShowHistory(true)} disabled={!online}>
              History
            </button>
          )}
        </div>
      </header>
      {notice && (
        <p className="notice editor-notice" role="status">
          {notice}
        </p>
      )}
      {showHistory && (
        <History
          docId={docId}
          onClose={() => setShowHistory(false)}
          onRestored={() => {
            setShowHistory(false);
            setNotice("Version restored. Your previous text is saved in History.");
          }}
        />
      )}
      <div className="column">
        <Title docId={docId} title={meta?.title ?? ""} readOnly={trashed} />
        {trashed && <p className="hint trashed-note">This document is in the Trash. Restore it to edit.</p>}
      </div>
      {denied ? (
        <p className="hint" role="alert">
          This document isn't available{denied === "not-found" ? " (it may have been deleted)" : ""}.
        </p>
      ) : session && ready ? (
        <Page
          key={session.id}
          session={session}
          readOnly={trashed}
          sync={sync}
          find={find}
          focusMode={focusMode}
          onOpenFind={() => openFind(false)}
          onCloseFind={() => setFind(null)}
        />
      ) : (
        <p className="hint">Opening…</p>
      )}
    </main>
  );
}

function Title({ docId, title, readOnly }: { docId: string; title: string; readOnly: boolean }) {
  const [value, setValue] = useState(title);
  useEffect(() => setValue(title), [title]);

  const save = () => {
    const next = value.trim();
    if (!next || next === title) return setValue(title);
    api.updateDocument(docId, { title: next }).catch(() => setValue(title));
  };

  return (
    <input
      className="doc-title"
      aria-label="Title"
      value={value}
      placeholder="Untitled"
      readOnly={readOnly}
      onChange={(e) => setValue(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

interface PageProps {
  session: DocSession;
  readOnly: boolean;
  sync: SyncClient;
  find: FindRequest | null;
  focusMode: boolean;
  onOpenFind(): void;
  onCloseFind(): void;
}

function Page({ session, readOnly, sync, find, focusMode, onOpenFind, onCloseFind }: PageProps) {
  const editor = useEditor(
    {
      extensions: [
        ...schemaExtensions(),
        Collaboration.configure({ document: session.ydoc, field: YDOC_FIELD }),
        Placeholder.configure({ placeholder: "Begin writing…" }),
        CharacterCount,
        Typography,
        Spellcheck,
        Find,
        FocusBlock,
        PreserveSpaces,
      ],
      editable: !readOnly,
      // Re-rendering React on every keystroke is the main cost in a 120k-word document.
      shouldRerenderOnTransaction: false,
      immediatelyRender: true,
      editorProps: {
        attributes: {
          class: "page prose",
          // Quill's own checker (spell/) replaces the browser's, which flags names.
          spellcheck: "false",
          autocapitalize: "sentences",
          role: "textbox",
          "aria-multiline": "true",
          "aria-label": "Document",
        },
      },
    },
    [session],
  );
  // Restored from (or moved to) Trash while open.
  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [editor, readOnly]);
  useKeyboardAwareCaret(editor);
  useTypewriter(editor, focusMode && !readOnly);

  return (
    // One element, so switching documents removes it whole: the menus' elements are moved
    // into <body> while shown, and React can't remove those one by one.
    <div className="page-host">
      {editor && !readOnly && <FormatMenu editor={editor} onFind={onOpenFind} />}
      {editor && !readOnly && <SpellMenu editor={editor} />}
      <EditorContent editor={editor} />
      {coarsePointer && <ScrollThumb />}
      {editor && find && <FindBar docId={session.id} editor={editor} readOnly={readOnly} request={find} onClose={onCloseFind} />}
      <StatusLine sync={sync} docId={session.id} editor={editor} readOnly={readOnly} />
    </div>
  );
}

const coarsePointer = window.matchMedia("(pointer: coarse)").matches;

/**
 * The formatting bar: a bubble menu over the selection by default, or docked (Settings) along
 * the bottom or, vertically, at the side, shown always or only while text is selected.
 */
function FormatMenu({ editor, onFind }: { editor: Editor; onFind(): void }) {
  const { always, side } = useFormatBar();
  if (!always && !side) {
    return (
      <BubbleMenu
        editor={editor}
        // On phones the system's copy/paste menu appears above the selection; stay below it.
        tippyOptions={{ placement: coarsePointer ? "bottom" : "top", duration: 0, maxWidth: "none" }}
        className="format-menu"
      >
        <FormatButtons editor={editor} onFind={onFind} />
      </BubbleMenu>
    );
  }
  return <DockedFormatMenu editor={editor} onFind={onFind} always={always} side={side} />;
}

function DockedFormatMenu(props: { editor: Editor; onFind(): void; always: boolean; side: boolean }) {
  const { editor, onFind, always, side } = props;
  const selecting = useEditorState({ editor, selector: ({ editor: e }) => e.isFocused && !e.state.selection.empty });
  const bar = useRef<HTMLDivElement>(null);

  // Side: keep the column clear of the bar (styles.css). Bottom: let the page scroll its
  // last lines (and the scroll thumb stop) above the bar.
  useEffect(() => {
    const root = document.documentElement;
    if (side) {
      root.dataset.formatBar = "side";
      return () => {
        delete root.dataset.formatBar;
      };
    }
    const observer = new ResizeObserver(([entry]) => root.style.setProperty("--format-h", `${entry!.borderBoxSize[0]!.blockSize}px`));
    observer.observe(bar.current!);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--format-h");
    };
  }, [side]);

  return (
    <div ref={bar} className={`format-menu docked ${side ? "side" : "bottom"}`} hidden={!always && !selecting}>
      <FormatButtons editor={editor} onFind={onFind} vertical={side} />
    </div>
  );
}

/** Bold, italic, H1, H2, quote, scene break, and Find and Copy on touch screens. */
function FormatButtons({ editor, onFind, vertical = false }: { editor: Editor; onFind(): void; vertical?: boolean }) {
  const active = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      h1: e.isActive("heading", { level: 1 }),
      h2: e.isActive("heading", { level: 2 }),
      quote: e.isActive("blockquote"),
      empty: e.state.selection.empty,
    }),
  });
  const run = (fn: (c: ReturnType<Editor["chain"]>) => ReturnType<Editor["chain"]>) => fn(editor.chain().focus()).run();
  return (
    <div role="toolbar" aria-label="Formatting" aria-orientation={vertical ? "vertical" : "horizontal"}>
      <FormatButton label="Bold" pressed={active.bold} onClick={() => run((c) => c.toggleBold())}>
        <b>B</b>
      </FormatButton>
      <FormatButton label="Italic" pressed={active.italic} onClick={() => run((c) => c.toggleItalic())}>
        <i>I</i>
      </FormatButton>
      <FormatButton label="Heading 1" pressed={active.h1} onClick={() => run((c) => c.toggleHeading({ level: 1 }))}>
        H1
      </FormatButton>
      <FormatButton label="Heading 2" pressed={active.h2} onClick={() => run((c) => c.toggleHeading({ level: 2 }))}>
        H2
      </FormatButton>
      <FormatButton label="Quote" pressed={active.quote} onClick={() => run((c) => c.toggleBlockquote())}>
        “
      </FormatButton>
      <FormatButton
        label="Scene break"
        // Insert after the selection rather than replacing it: never delete her words.
        onClick={() => run((c) => c.setTextSelection(editor.state.selection.to).setHorizontalRule())}
      >
        ⁂
      </FormatButton>
      {coarsePointer && (
        // Phones have no Ctrl+F; this searches for the selected text, if any.
        <FormatButton label="Find" onClick={onFind}>
          <svg className="find-icon" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="10.5" cy="10.5" r="6" />
            <path d="M15 15l5 5" />
          </svg>
        </FormatButton>
      )}
      {coarsePointer && <CopyButton editor={editor} disabled={active.empty} />}
    </div>
  );
}

/**
 * On touch screens the system's own Copy needs a long press and a hunt through its menu,
 * so the bubble menu offers one. Copies the same rich text and plain text a Ctrl+C would.
 */
function CopyButton({ editor, disabled }: { editor: Editor; disabled: boolean }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = () => {
    const { dom, text } = editor.view.serializeForClipboard(editor.state.selection.content());
    const write =
      typeof ClipboardItem === "undefined"
        ? navigator.clipboard.writeText(text)
        : navigator.clipboard.write([
            new ClipboardItem({
              "text/html": new Blob([dom.innerHTML], { type: "text/html" }),
              "text/plain": new Blob([text], { type: "text/plain" }),
            }),
          ]);
    write.then(
      () => setCopied(true),
      () => undefined,
    );
  };

  return (
    <button
      type="button"
      className="copy"
      aria-label="Copy"
      title="Copy"
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={copy}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

/** Shown when the caret is in an underlined word: suggestions and "Add to dictionary". */
function SpellMenu({ editor }: { editor: Editor }) {
  const target = useEditorState({
    editor,
    selector: ({ editor: e }) => misspellingAtCaret(e.state),
    equalityFn: (a, b) => a?.from === b?.from && a?.to === b?.to && a?.word === b?.word,
  });
  const word = target?.word ?? null;
  const [suggestions, setSuggestions] = useState<{ word: string; list: string[] } | null>(null);
  useEffect(() => {
    if (!word) return;
    let live = true;
    void suggest(word).then((list) => live && setSuggestions({ word, list }));
    return () => {
      live = false;
    };
  }, [word]);
  const list = suggestions?.word === word ? suggestions.list : null;

  const replace = (text: string) => {
    if (!target) return;
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.insertText(text, target.from, target.to);
        return true;
      })
      .run();
  };

  return (
    <BubbleMenu
      editor={editor}
      pluginKey="spellMenu"
      shouldShow={({ view, state }) => view.hasFocus() && misspellingAtCaret(state) !== null}
      tippyOptions={{ placement: "bottom", duration: 0, maxWidth: "none" }}
      className="format-menu spell-menu"
    >
      <div role="toolbar" aria-label="Spelling">
        {list === null ? (
          <span className="spell-note">…</span>
        ) : list.length ? (
          list.map((s) => (
            <button key={s} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => replace(s)}>
              {s}
            </button>
          ))
        ) : (
          <span className="spell-note">No suggestions</span>
        )}
        <button
          type="button"
          className="spell-add"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => word && addToDictionary(word)}
        >
          Add to dictionary
        </button>
      </div>
    </BubbleMenu>
  );
}

function FormatButton(props: { label: string; pressed?: boolean; onClick(): void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={props.label}
      title={props.label}
      aria-pressed={props.pressed}
      // Keep the selection: don't let the button take focus on mousedown.
      onMouseDown={(e) => e.preventDefault()}
      onClick={props.onClick}
    >
      {props.children}
    </button>
  );
}

const numberFormat = new Intl.NumberFormat();
const WORD_COUNT_DELAY_MS = 700;

/**
 * Total words, recounted once typing pauses (counting 120k words per keystroke would lag).
 * Each change in the count is reported as words written, except changes from elsewhere
 * (another device, a restored version, loading the document), which are settled around.
 */
function useWordCount(editor: Editor | null): { total: number | null; selected: number } {
  const [total, setTotal] = useState<number | null>(null);
  const [selected, setSelected] = useState(0);
  useEffect(() => {
    if (!editor) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const words = (node?: PMNode) => editor.storage.characterCount.words(node && { node }) as number;
    /** The count this device's writing is measured from; null after a change from elsewhere. */
    let last: number | null = null;
    /** This device changed the text since `last`. */
    let typed = false;
    const count = () => {
      const now = words();
      if (typed && last !== null) recordWritten(now - last);
      last = now;
      typed = false;
      setTotal(now);
    };
    const schedule = ({ transaction }: { transaction: Transaction }) => {
      const sync = transaction.getMeta(ySyncPluginKey) as { isChangeOrigin?: boolean; isUndoRedoOperation?: boolean } | undefined;
      // Undo and redo also come through Yjs, but they're this device's writing.
      if (sync?.isChangeOrigin && !sync.isUndoRedoOperation) {
        // Rare, so counting here is fine: settle the typing before it, measure again after.
        if (typed && last !== null) recordWritten(words(transaction.before) - last);
        last = null;
        typed = false;
      } else {
        if (last === null) last = words(transaction.before);
        typed = true;
      }
      clearTimeout(timer);
      timer = setTimeout(count, WORD_COUNT_DELAY_MS);
    };
    const selection = () => {
      const { from, to, empty } = editor.state.selection;
      setSelected(empty ? 0 : editor.state.doc.textBetween(from, to, " ", " ").split(/\s+/).filter(Boolean).length);
    };
    count();
    editor.on("update", schedule);
    editor.on("selectionUpdate", selection);
    return () => {
      clearTimeout(timer);
      // Leaving the document: count what was typed since the last pause.
      if (!editor.isDestroyed) count();
      editor.off("update", schedule);
      editor.off("selectionUpdate", selection);
    };
  }, [editor]);
  return { total, selected };
}

function StatusLine(props: { sync: SyncClient; docId: string; editor: Editor | null; readOnly: boolean }) {
  const { sync, docId, editor, readOnly } = props;
  const state = useSyncState(sync, docId);
  const { total, selected } = useWordCount(editor);
  const [showProgress, setShowProgress] = useState(false);
  const history = useEditorState({
    editor,
    selector: (ctx) => ({ undo: !!ctx.editor?.can().undo(), redo: !!ctx.editor?.can().redo() }),
  });
  const label = { synced: "Saved", syncing: "Saving…", offline: "Offline: saved on this device" }[state];
  const words = total === null ? "" : `${selected ? `${numberFormat.format(selected)} of ` : ""}${numberFormat.format(total)} ${total === 1 ? "word" : "words"}`;

  return (
    <footer className="status-line">
      {!readOnly && (
        <div className="history-buttons">
          <button
            type="button"
            aria-label="Undo"
            disabled={!history?.undo}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor?.commands.undo()}
          >
            ↶
          </button>
          <button
            type="button"
            aria-label="Redo"
            disabled={!history?.redo}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor?.commands.redo()}
          >
            ↷
          </button>
        </div>
      )}
      <span className="word-count" aria-live="off">
        {words}
      </span>
      <ProgressButton onOpen={() => setShowProgress(true)} />
      {showProgress && <ProgressSheet onClose={() => setShowProgress(false)} />}
      <span className={`sync-dot ${state}`} data-sync-state={state} role="img" aria-label={label} title={label} />
    </footer>
  );
}
